/**
 * auto-bonus.js — automatic free bonus products
 * Theme: Sense 8.0.1
 *
 * Adds specific digital course variants to the cart automatically when a
 * qualifying trigger variant is present, and removes them again when no
 * remaining trigger requires them.
 *
 * PRICING IS NOT HANDLED HERE. Bonuses are discounted entirely by Shopify
 * Automatic Discounts (Buy X Get Y). This file must never set, override or
 * compute a price. It only adds and removes lines.
 *
 * Design: this is a reconciler, not a click handler. Every run reads the cart
 * fresh from /cart.js and converges it towards the mapping below. That makes
 * page load, drawer, /cart page, quick add and double clicks all one code path.
 *
 * Integration: attaches through the theme's existing pubsub ('cart-update').
 * No theme file is modified other than the script tag in layout/theme.liquid.
 */

(function () {
  'use strict';

  // ───────────────────────────────────────────────────────────────────────────
  // Trigger variant  →  bonus variants, added automatically at quantity 1.
  //
  // Any change here MUST be mirrored by a matching Buy X Get Y automatic
  // discount in the Shopify admin. Without the matching discount the bonus is
  // still added to the cart and the customer is charged full price for it.
  // ───────────────────────────────────────────────────────────────────────────
  var MAP = {
    // Planners
    52777189605717: [54522094387541],
    52777189638485: [54522094387541],
    40616621310110: [54522094387541],
    42052827807902: [54522094387541],
    42052827840670: [54522094387541],

    // Business Trio
    54504911536469: [54373305549141, 54484173226325, 54484183482709],
    54504911569237: [54373305549141, 54484173226325, 54484183482709],
    54504911602005: [54373305549141, 54484173226325, 54484183482709],

    // SSB.BOSS planner — shares two bonuses with the Business Trio above but
    // NOT its third (54373305549141). A cart holding both still shows each
    // shared bonus once, and removing one trigger leaves them in place while
    // the other still requires them.
    51109333795157: [54484173226325, 54484183482709],
    53782017573205: [54484173226325, 54484183482709],
    51109333762389: [54484173226325, 54484183482709],

    // The Money Game
    46404516053333: [54522113032533, 51871060099413],
    46404516118869: [54522113032533, 51871060099413],
    48246633562453: [54522113032533, 51871060099413],
    47345824498005: [54522113032533, 51871060099413],
  };

  // All bonus products are digital: requires_shipping false, no inventory
  // tracking. There is deliberately no availability or stock-aware logic here.

  var PROP = '_auto_bonus';
  var PROP_VALUE = 'true';

  var DEBOUNCE_MS = 60; // cart-update fires before the theme renders; let it render first
  var MODAL_REFRESH_MS = 300; // quick-add modal renders on a setTimeout after modalClosed
  var REQUEST_TIMEOUT_MS = 10000; // no cart request may latch the mutex longer than this
  var ELEMENT_WAIT_MS = 5000; // cap on waiting for the theme's cart elements to upgrade

  // Statuses that mean "this variant is genuinely unaddable": deleted/unpublished
  // (404) or rejected as invalid (422). Everything else — 429 rate limiting, 5xx,
  // proxy errors, bot challenges — is transient and must be retried, never
  // blocklisted, or one bad moment disables the bonus for the whole session.
  var PERMANENT_ADD_FAILURES = [404, 422];

  var SS_DISMISS_PREFIX = 'dismissed_bonus:'; // dismissed_bonus:{trigger}:{bonus}
  var SS_SEEN = 'auto_bonus_seen_v1'; // bonuses present as _auto_bonus lines last run
  var SS_FAILED = 'auto_bonus_failed_v1'; // variants that failed to add this session

  var LOG = '[auto-bonus]';

  if (!window.routes || !routes.cart_add_url || !routes.cart_update_url || !routes.cart_url) {
    console.warn(LOG, 'window.routes unavailable, auto bonuses disabled');
    return;
  }

  // ───────────────────────────────────────────────────────────────── storage ──
  // sessionStorage throws in Safari private mode, in sandboxed iframes, and on
  // quota exhaustion. Silently dropping writes is NOT safe here: with no memory
  // of what was present, a deleted bonus is re-added on every cart update and
  // the customer cannot remove it. So we fall back to an in-memory store that
  // lasts for the page view — dismissals stop surviving navigation, but the
  // delete-and-it-comes-back livelock cannot happen.

  var memory = {};
  var useMemory = false;

  function ssGet(key) {
    if (!useMemory) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        useMemory = true;
      }
    }
    return Object.prototype.hasOwnProperty.call(memory, key) ? memory[key] : null;
  }

  function ssSet(key, value) {
    memory[key] = String(value);
    if (useMemory) return;
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      useMemory = true;
    }
  }

  function ssRemove(key) {
    delete memory[key];
    if (useMemory) return;
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      useMemory = true;
    }
  }

  function ssKeys() {
    if (!useMemory) {
      try {
        return Object.keys(window.sessionStorage);
      } catch (e) {
        useMemory = true;
      }
    }
    return Object.keys(memory);
  }

  function readList(key) {
    var raw = ssGet(key);
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(key, list) {
    ssSet(key, JSON.stringify(list.map(String)));
  }

  // ─────────────────────────────────────────────────────────────── dismissal ──
  // DECISION — dismissal is scoped to the (trigger, bonus) pair, deliberately.
  //
  // If the customer deletes bonus B while trigger T1 is in the cart, we write
  // dismissed_bonus:T1:B. If they later add T2, which also requires B, B IS
  // re-added — no key exists for T2. A new qualifying purchase is a new offer.
  //
  // This is intentional, not a leak in the scoping. The alternative (a global
  // per-bonus flag) would mean one refusal permanently suppresses B across
  // every future purchase in the session, including products the customer has
  // not bought yet.
  //
  // It cannot loop: when a bonus is deleted we write a dismissal key for EVERY
  // trigger currently in the cart that requires it. So the second delete
  // silences T1 and T2 both, and B only returns if a genuinely new trigger is
  // added later.
  //
  // Keys are cleared as soon as their trigger leaves the cart, so removing and
  // re-adding the trigger restores the bonus.
  //
  // Scope note: sessionStorage is per tab, so both the dismissal keys and the
  // SS_SEEN snapshot start empty in a new tab. localStorage would share them but
  // would also make a refusal outlive the session, which is worse — the bonus
  // would go silently missing days later with no way to recover it.
  //
  // Because SS_SEEN now records every bonus variant in the cart, a tab populates
  // it on its page-load reconcile and on every cart update. A customer can only
  // delete a line the tab has rendered, and rendering it requires a page load or
  // a cart update in that tab — both of which write SS_SEEN first. So a deletion
  // is detected in the tab where it happens, and that tab writes its own
  // dismissal keys. Tab A's deletion also reaches tab B implicitly: B recorded
  // the bonus while it was present, so B's next reconcile reads the absence as a
  // deletion and dismisses it rather than re-adding.
  //
  // Residual, not fixed: a tab that has never reconciled while the bonus was
  // present — opened before the bonus existed and not updated since — has no
  // record of it. If a reconcile is triggered there after the bonus was deleted
  // in another tab, it reads "never added" and re-adds it once. Closing that
  // would require sharing state across tabs (localStorage or BroadcastChannel),
  // which we deliberately are not doing.

  function dismissKey(triggerId, bonusId) {
    return SS_DISMISS_PREFIX + triggerId + ':' + bonusId;
  }

  function isDismissed(triggerId, bonusId) {
    return ssGet(dismissKey(triggerId, bonusId)) === '1';
  }

  function setDismissed(triggerId, bonusId) {
    ssSet(dismissKey(triggerId, bonusId), '1');
  }

  function clearStaleDismissals(variantsInCart) {
    ssKeys().forEach(function (key) {
      if (key.indexOf(SS_DISMISS_PREFIX) !== 0) return;
      var trigger = key.slice(SS_DISMISS_PREFIX.length).split(':')[0];
      if (!variantsInCart.has(trigger)) ssRemove(key);
    });
  }

  // ─────────────────────────────────────────────────────────────── blocklist ──
  // A variant that Shopify refuses to add with a 4xx (deleted, unpublished, not
  // sellable in this market) is remembered for the session so we do not retry on
  // every cart update. 5xx and network errors are transient and must NOT block:
  // they are logged and retried on the next cart update.

  function isBlocked(bonusId) {
    return readList(SS_FAILED).indexOf(String(bonusId)) !== -1;
  }

  function block(bonusId) {
    var list = readList(SS_FAILED);
    if (list.indexOf(String(bonusId)) === -1) {
      list.push(String(bonusId));
      writeList(SS_FAILED, list);
    }
  }

  // ───────────────────────────────────────────────────────────────── helpers ──

  function variantIdOf(item) {
    return String(item.variant_id != null ? item.variant_id : item.id);
  }

  function bonusesFor(variantId) {
    return Object.prototype.hasOwnProperty.call(MAP, variantId) ? MAP[variantId] : null;
  }

  function isAutoBonusLine(item) {
    return !!(item.properties && String(item.properties[PROP]) === PROP_VALUE);
  }

  // Every variant that appears as a bonus anywhere in the map.
  var ALL_BONUSES = new Set();
  Object.keys(MAP).forEach(function (triggerId) {
    MAP[triggerId].forEach(function (bonusId) {
      ALL_BONUSES.add(String(bonusId));
    });
  });

  // Deduplicated list of bonus variants present in a set of line items,
  // regardless of whether we added them or the customer bought them.
  function bonusVariantsIn(items) {
    var out = [];
    items.forEach(function (item) {
      var id = variantIdOf(item);
      if (!ALL_BONUSES.has(id)) return;
      if (out.indexOf(id) === -1) out.push(id);
    });
    return out;
  }

  function cartIsEmpty() {
    // The count bubble is only rendered when cart != empty (sections/header.liquid).
    // Lets a page load with an empty cart skip the /cart.js request entirely.
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon) return false;
    return !icon.querySelector('.cart-count-bubble');
  }

  // Every request is bounded. Without this, a fetch that never settles (mobile
  // network handover, captive portal, a proxy holding the connection) leaves the
  // mutex latched for the life of the page and the reconciler silently dead.
  // The race is belt-and-braces for browsers without AbortController: it rejects
  // even if the underlying request cannot be cancelled.
  function fetchWithTimeout(url, options) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var opts = Object.assign({}, options || {});
    if (controller) opts.signal = controller.signal;

    var timer = null;
    var timeout = new Promise(function (_resolve, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('request timed out after ' + REQUEST_TIMEOUT_MS + 'ms: ' + url));
      }, REQUEST_TIMEOUT_MS);
    });

    return Promise.race([fetch(url, opts), timeout]).then(
      function (response) {
        clearTimeout(timer);
        return response;
      },
      function (error) {
        clearTimeout(timer);
        throw error;
      }
    );
  }

  function readCart() {
    return fetchWithTimeout(routes.cart_url + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then(function (response) {
      if (!response.ok) throw new Error('GET cart.js failed: ' + response.status);
      return response.json();
    });
  }

  // ───────────────────────────────────────────────────────────── section i/o ──
  // Render targets are read from the theme's own cart elements rather than
  // hardcoded, so the dynamic section ids on /cart stay correct. Both the
  // drawer and the cart page are collected: on /cart both exist in the DOM and
  // both need refreshing from a single request.

  function collectTargets() {
    var out = [];
    var seen = {};
    document.querySelectorAll('cart-drawer-items, cart-items').forEach(function (element) {
      var list;
      try {
        list = element.getSectionsToRender();
      } catch (e) {
        return; // element not upgraded yet, or its section is not on this page
      }
      (list || []).forEach(function (target) {
        if (!target || !target.section || !target.id) return;
        var key = target.id + '|' + target.section + '|' + (target.selector || '');
        if (seen[key]) return;
        seen[key] = true;
        out.push(target);
      });
    });
    return out;
  }

  function sectionInnerHTML(html, selector) {
    try {
      var found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector || '.shopify-section');
      return found ? found.innerHTML : null;
    } catch (e) {
      return null;
    }
  }

  // Mirrors CartItems.updateQuantity's replacement logic exactly, so a refresh
  // driven from here is indistinguishable from one driven by the theme.
  function applySections(sections, targets) {
    if (!sections) return;
    targets.forEach(function (target) {
      var html = sections[target.section];
      if (typeof html !== 'string') return;
      var root = document.getElementById(target.id);
      if (!root) return;
      var container = (target.selector && root.querySelector(target.selector)) || root;
      var inner = sectionInnerHTML(html, target.selector);
      if (inner === null) return;
      container.innerHTML = inner;
    });
  }

  // CartItems.updateQuantity toggles these three classes itself (cart.js:111-116)
  // and applySections cannot: it only swaps innerHTML. Without this, a removal
  // that empties the cart leaves /cart showing an empty table with a live
  // Checkout button, and leaves the drawer a blank panel.
  function applyEmptyState(itemCount) {
    var isEmpty = itemCount === 0;
    document.querySelectorAll('cart-items, cart-drawer-items').forEach(function (element) {
      element.classList.toggle('is-empty', isEmpty);
    });
    var drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', isEmpty);
    var footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', isEmpty);
  }

  // The theme removes lines by position (cart.js:8 reads data-index, posts
  // /cart/change.js {line}). We remove by line key. While our request is in
  // flight the server has already shifted the indices but the rendered
  // data-index values have not caught up, so a click landing in that window
  // deletes the wrong line. This is the theme's own mitigation for its own
  // writes (CartItems.enableLoading) — .cart__items--disabled is
  // pointer-events: none in component-cart.css.
  function setCartBusy(busy) {
    [document.getElementById('main-cart-items'), document.getElementById('CartDrawer-CartItems')].forEach(
      function (element) {
        if (element) element.classList.toggle('cart__items--disabled', busy);
      }
    );
  }

  // Section Rendering API pull, used when the cart is already correct but the
  // DOM may have been painted from a stale response.
  function refreshSections() {
    var targets = collectTargets();
    if (!targets.length) return Promise.resolve();
    var names = targets
      .map(function (target) {
        return target.section;
      })
      .join(',');
    return fetchWithTimeout(window.location.pathname + '?sections=' + names, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(function (response) {
        if (!response.ok) throw new Error('section refresh failed: ' + response.status);
        return response.json();
      })
      .then(function (sections) {
        applySections(sections, targets);
      });
  }

  function sectionRequestBody(targets) {
    if (!targets.length) return {};
    return {
      sections: targets.map(function (target) {
        return target.section;
      }),
      sections_url: window.location.pathname,
    };
  }

  // snippets/cart-script.mczr.liquid patches window.fetch and caches whatever
  // comes back from a /cart/* URL as its idea of the cart. An add.js response is
  // not a cart object, so after mutating we hand it a real one. Only runs on
  // /cart, where that snippet is rendered.
  function repairMyCustomizerSnapshot() {
    if (!window.MyCustomizerCart) return Promise.resolve();
    return readCart().catch(function () {
      /* best effort only */
    });
  }

  // ─────────────────────────────────────────────────────────────── cart i/o ──

  // Never assumes a JSON body. Shopify's throttle pages, bot challenges and any
  // intercepting proxy return HTML; response.json() would reject and that
  // rejection is indistinguishable from a real failure further up the chain.
  function postJSON(url, body) {
    return fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.text().then(function (text) {
        var parsed = null;
        try {
          parsed = text ? JSON.parse(text) : null;
        } catch (e) {
          parsed = null;
        }
        return { ok: response.ok, status: response.status, body: parsed || {} };
      });
    });
  }

  function addBonuses(bonusIds, targets) {
    var body = Object.assign(
      {
        items: bonusIds.map(function (id) {
          var properties = {};
          properties[PROP] = PROP_VALUE;
          return { id: Number(id), quantity: 1, properties: properties };
        }),
      },
      sectionRequestBody(targets)
    );

    return postJSON(routes.cart_add_url, body).then(function (result) {
      if (result.ok) return { addedIds: bonusIds.slice(), sections: result.body.sections };

      var description = result.body && result.body.description;
      console.warn(LOG, 'add rejected', result.status, description, bonusIds.join(','));

      // Transient (429, 5xx, proxy/challenge pages): do not blocklist, and do
      // not fan the batch out into N more failing requests — that amplifies load
      // on an endpoint that is already refusing us. The next cart update retries.
      if (PERMANENT_ADD_FAILURES.indexOf(result.status) === -1) {
        return { addedIds: [], sections: null };
      }

      // 404/422 on a single id: the variant is genuinely unaddable this session.
      if (bonusIds.length === 1) {
        block(bonusIds[0]);
        return { addedIds: [], sections: null };
      }

      // 404/422 on a batch: /cart/add.js is atomic, so one bad variant rejected
      // the whole thing. Retry the ids individually so a single broken mapping
      // cannot suppress the rest, and blocklist only the ones that really fail.
      return addBonusesSequentially(bonusIds, targets);
    });
  }

  function addBonusesSequentially(bonusIds, targets) {
    var addedIds = [];
    var sections = null;
    return bonusIds
      .reduce(function (chain, id) {
        return chain.then(function () {
          return addBonuses([id], targets)
            .then(function (result) {
              if (!result.addedIds.length) return;
              addedIds = addedIds.concat(result.addedIds);
              if (result.sections) sections = result.sections;
            })
            .catch(function (error) {
              // One rejected step must not discard the ids already added, or the
              // cart holds a bonus that SS_SEEN never records.
              console.warn(LOG, 'add failed for', id, error);
            });
        });
      }, Promise.resolve())
      .then(function () {
        return { addedIds: addedIds, sections: sections };
      });
  }

  function removeLines(lines, targets) {
    var updates = {};
    lines.forEach(function (line) {
      updates[line.key] = 0;
    });

    // Only `updates` is sent. The invoice fields in cart-drawer.liquid and
    // main-cart-footer.liquid keep their state in cart attributes; including
    // an `attributes` key here would wipe them.
    var body = Object.assign({ updates: updates }, sectionRequestBody(targets));

    return postJSON(routes.cart_update_url, body).then(function (result) {
      if (result.ok) return { mutated: true, sections: result.body.sections };
      console.warn(LOG, 'bonus removal rejected', result.status, result.body && result.body.description);
      return { mutated: false, sections: null };
    });
  }

  // ────────────────────────────────────────────────────────────── reconciler ──

  function reconcile(forceRender) {
    return readCart().then(function (cart) {
      var items = cart.items || [];
      var inCart = new Set(
        items.map(function (item) {
          return variantIdOf(item);
        })
      );

      clearStaleDismissals(inCart);

      // bonus id → set of trigger ids in the cart that require it
      var required = new Map();
      items.forEach(function (item) {
        var bonuses = bonusesFor(variantIdOf(item));
        if (!bonuses) return;
        bonuses.forEach(function (bonus) {
          var key = String(bonus);
          if (!required.has(key)) required.set(key, new Set());
          required.get(key).add(variantIdOf(item));
        });
      });

      var autoLines = items.filter(isAutoBonusLine);

      // Deletion detection. A required bonus that is absent now but was present
      // at the end of the previous run was deleted by the customer — the only
      // other way it could vanish is our own removal, and we only remove
      // bonuses that are no longer required.
      //
      // SS_SEEN records EVERY bonus variant in the cart, not just _auto_bonus
      // lines. A customer-owned copy that gets deleted while its trigger is in
      // the cart must count as a deletion too, otherwise it would be re-added as
      // an auto bonus — the reappear-on-delete behaviour we rejected. Removal
      // logic is unaffected: only _auto_bonus lines are ever removed.
      var seenBefore = readList(SS_SEEN);
      required.forEach(function (triggers, bonusId) {
        if (inCart.has(bonusId)) return;
        if (seenBefore.indexOf(bonusId) === -1) return;
        triggers.forEach(function (triggerId) {
          setDismissed(triggerId, bonusId);
        });
      });

      var toAdd = [];
      required.forEach(function (triggers, bonusId) {
        if (inCart.has(bonusId)) return; // already there, auto or bought manually
        if (isBlocked(bonusId)) return;
        // Suppressed only when EVERY trigger currently requiring this bonus has
        // been dismissed. One un-dismissed trigger is a new offer and re-adds
        // it — see the dismissal decision note above.
        var dismissed = triggers.size > 0;
        triggers.forEach(function (triggerId) {
          if (!isDismissed(triggerId, bonusId)) dismissed = false;
        });
        if (dismissed) return;
        toAdd.push(bonusId);
      });

      // Only _auto_bonus lines are ever removed, and only when no remaining
      // trigger in the cart requires them.
      var toRemove = autoLines.filter(function (line) {
        return !required.has(variantIdOf(line));
      });

      if (!toAdd.length && !toRemove.length) {
        writeList(SS_SEEN, bonusVariantsIn(items));
        return forceRender ? refreshSections() : undefined;
      }

      var targets = collectTargets();
      var addedIds = [];
      var removedKeys = {};
      var sections = null;

      // Lock cart interaction for the duration of the write, then release it in
      // every exit path including failure.
      setCartBusy(true);

      var work = Promise.resolve();

      if (toRemove.length) {
        work = work.then(function () {
          return removeLines(toRemove, targets)
            .then(function (result) {
              if (!result.mutated) return;
              toRemove.forEach(function (line) {
                removedKeys[line.key] = true;
              });
              sections = result.sections || sections;
            })
            .catch(function (error) {
              // A rejected stage must not skip the snapshot and repaint below,
              // or the DOM keeps showing lines the server has already dropped.
              console.warn(LOG, 'removal failed', error);
            });
        });
      }

      if (toAdd.length) {
        work = work.then(function () {
          return addBonuses(toAdd, targets)
            .then(function (result) {
              addedIds = result.addedIds;
              if (result.addedIds.length) sections = result.sections || sections;
            })
            .catch(function (error) {
              console.warn(LOG, 'add failed', error);
            });
        });
      }

      return work.then(function () {
        var mutated = addedIds.length > 0 || Object.keys(removedKeys).length > 0;

        // Snapshot the state we expect to be live now, derived from the lines
        // that survive plus the ids that were genuinely added. Adds that failed
        // — for any reason, blocked or transient — are excluded, so a failure is
        // never mistaken for a customer deletion on the next run.
        var expected = bonusVariantsIn(
          items.filter(function (item) {
            return !removedKeys[item.key];
          })
        );
        addedIds.forEach(function (id) {
          if (expected.indexOf(id) === -1) expected.push(id);
        });
        writeList(SS_SEEN, expected);

        // Derived from the lines that survive plus one unit per added bonus,
        // so the empty state is correct even when the response carried no cart.
        var itemCount =
          items.reduce(function (total, item) {
            return removedKeys[item.key] ? total : total + item.quantity;
          }, 0) + addedIds.length;

        if (mutated && sections) {
          applySections(sections, targets);
          applyEmptyState(itemCount);
          mutatedSinceModalClose = true;
          return repairMyCustomizerSnapshot();
        }
        if (mutated || forceRender) {
          // Mutation succeeded but sections came back empty, or a stale render
          // is suspected. Pull the markup separately.
          mutatedSinceModalClose = mutatedSinceModalClose || mutated;
          return refreshSections().then(function () {
            if (!mutated) return undefined;
            applyEmptyState(itemCount);
            return repairMyCustomizerSnapshot();
          });
        }
        return undefined;
      });
    });
  }

  // ──────────────────────────────────────────────────── scheduling and races ──
  // One reconcile at a time (mutex), with at most one trailing run coalesced
  // from anything that arrives mid-flight. Every run re-reads /cart.js, so the
  // result converges even against writes made by theme code we do not control.

  var running = false;
  var queued = false;
  var staleSuspected = false;
  var timer = null;
  var mutatedSinceModalClose = false;

  function run() {
    if (running) {
      queued = true;
      return;
    }
    running = true;

    var force = staleSuspected;
    staleSuspected = false;

    // Gated here rather than at init, so EVERY path — page load and every
    // cart-update — is guaranteed upgraded elements before collectTargets runs.
    whenCartElementsReady()
      .then(function () {
        return reconcile(force);
      })
      .catch(function (error) {
        // Never block or break the customer's own cart action.
        console.warn(LOG, 'reconcile failed', error);
      })
      .then(function () {
        // Released here, not in reconcile, so a timeout or rejection can never
        // leave cart interaction locked.
        setCartBusy(false);
        running = false;
        if (queued) {
          queued = false;
          setTimeout(run, 0);
        }
      });
  }

  function schedule() {
    if (running) {
      // A cart update landed while we were mid-flight: the theme may have just
      // painted markup that predates our mutation. Force a refresh next run.
      queued = true;
      staleSuspected = true;
      return;
    }
    clearTimeout(timer);
    timer = setTimeout(run, DEBOUNCE_MS);
  }

  // ──────────────────────────────────────────────────────────────────── init ──

  // constants.js declares PUB_SUB_EVENTS with a top-level `const`, which binds
  // in the global lexical environment and NOT on the global object — so
  // window.PUB_SUB_EVENTS is undefined even though the identifier resolves.
  // Referencing it bare, the way the theme's own files do, is the only test that
  // works. (window.routes is fine: theme.liquid assigns it explicitly.)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    // Fired by product-form.js after an add and by cart.js after a quantity
    // change or removal. We never publish, so this cannot re-enter.
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      schedule();
    });
  } else {
    console.warn(LOG, 'pubsub unavailable, falling back to page-load reconcile only');
  }

  // quick-add-modal defers its render until modalClosed and renders from the
  // response captured before our bonus add, which would visually drop the
  // bonus. Pull fresh markup once the modal's own render has run.
  //
  // ModalDialog.hide dispatches modalClosed on document.body for EVERY modal in
  // the theme — image lightbox, video, password — with no reference to the modal
  // itself, so event.target is always document.body and cannot distinguish them.
  // It does dispatch before removeAttribute('open') (global.js:516-517), so the
  // closing modal is still queryable while our listener runs. Anything that is
  // not the quick-add modal must be ignored, or an unrelated lightbox repaints
  // the drawer and destroys whatever the customer was typing into it.
  document.body.addEventListener('modalClosed', function () {
    if (!document.querySelector('quick-add-modal[open]')) return;
    if (!mutatedSinceModalClose) return;
    mutatedSinceModalClose = false;
    setTimeout(function () {
      refreshSections().catch(function (error) {
        console.warn(LOG, 'post-modal refresh failed', error);
      });
    }, MODAL_REFRESH_MS);
  });

  // cart.js and cart-drawer.js are deferred scripts in <body>; this file is a
  // deferred script in <head>, so it executes first and the cart custom elements
  // are not upgraded yet. collectTargets would find the elements but no
  // getSectionsToRender, silently yielding no render targets — the bonus would be
  // added server-side and the DOM never updated. Waiting for DOMContentLoaded is
  // not sufficient on its own; the definitions are what we actually need.
  var elementsReady = null;

  function whenCartElementsReady() {
    if (elementsReady) return elementsReady;
    if (!window.customElements || typeof customElements.whenDefined !== 'function') {
      elementsReady = Promise.resolve();
      return elementsReady;
    }
    var waits = ['cart-items', 'cart-drawer-items'].map(function (name) {
      // Raced with a cap: a theme where one of these is never defined (no drawer,
      // no cart section on this template) must not stall the reconciler forever.
      return Promise.race([
        customElements.whenDefined(name),
        new Promise(function (resolve) {
          setTimeout(resolve, ELEMENT_WAIT_MS);
        }),
      ]);
    });
    elementsReady = Promise.all(waits);
    return elementsReady;
  }

  function init() {
    // Covers page refresh, non-AJAX adds and carts changed in another tab.
    if (cartIsEmpty()) {
      // No trigger can be in an empty cart, so every dismissal key is stale.
      // clearStaleDismissals normally does this inside a reconcile, which we skip
      // when the cart is empty — and without this a dismissal survives checkout
      // and silently suppresses the bonus on the customer's next order in the
      // same tab.
      clearStaleDismissals(new Set());
      writeList(SS_SEEN, []);
      return;
    }
    schedule(); // run() waits for the cart elements before it touches the DOM
  }

  // Deferred scripts execute with readyState 'interactive', so this branch is
  // effectively always the else — kept for correctness if the file is ever
  // loaded some other way.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
